import React, { useEffect, useMemo, useState, useRef } from 'react';
import { get } from '@/features/shared/lib/api';
import axios from 'axios';

/**
 * ResearcherIdIdsPanel
 * - Single component that contains both:
 *   1) "Researcher ID 추가하기" (유형 + 아이디 + 추가하기)
 *   2) "추가된 Researcher ID 리스트" (수정/저장/취소/삭제)
 * - Uses user endpoints for list: POST /wp-json/rm/v1/user/scholar/{record_id}/ids/{insert|update|delete} (multipart/form-data)
 */
export default function ResearcherIdIdsPanel({ record, showAdd = true, addOptions: propAddOptions, optLoading: propOptLoading, optError: propOptError } = {}) {
  const r = record || {};

  // ------------------------------
  // Options for type select (add form)
  // ------------------------------
  const [addOptions, setAddOptions] = useState(Array.isArray(propAddOptions) && propAddOptions.length ? propAddOptions : []);

  // Sync from parent when provided
  useEffect(() => {
    if (Array.isArray(propAddOptions) && propAddOptions.length) {
      setAddOptions(propAddOptions);
    }
  }, [propAddOptions, propOptLoading, propOptError]);

  // Fallback self-fetch only when parent didn't provide options
  useEffect(() => {
    if (Array.isArray(propAddOptions) && propAddOptions.length) return;
    let alive = true;
    (async () => {
      try {
        setOptLoading(true);
        setOptError('');
        const res = await get('/rm/v1/code/search/scholar_id_type');
        const records = res?.result?.records || res?.records || res?.result || [];
        const mapped = Array.isArray(records)
          ? records
              .filter((it) => it && (it.disp_yn || 'Y') === 'Y' && (it.del_yn || 'N') !== 'Y' && it.code_id)
              .sort((a, b) => (Number(a?.visible_seq) || 9999) - (Number(b?.visible_seq) || 9999))
              .map((it) => ({
                value: it.code_id,
                label: it.code_name || it.code_name_eng || it.code_id,
                map_code: it.map_code || '',
              }))
          : [];
        if (!alive) return;
        setAddOptions(mapped.length ? [{ value: '', label: '선택해주세요.' }, ...mapped] : addOptionsFallback);
      } catch (e) {
        if (!alive) return;
        setOptError(e?.response?.data?.message || e?.message || '코드 목록을 불러오지 못했습니다.');
        setAddOptions(addOptionsFallback);
      } finally {
        if (alive) setOptLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [propAddOptions]);

  // For label rendering in list when row.label is missing
  const labelMap = useMemo(() => {
    const map = {};
    (addOptions || []).forEach((opt) => { if (opt && opt.value) map[opt.value] = opt.label; });
    return map;
  }, [addOptions]);

  // ------------------------------
  // Added IDs list state
  // ------------------------------
  const [list, setList] = useState(Array.isArray(r.scholar_id_type) ? r.scholar_id_type : []);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Sync list when parent-provided record.scholar_id_type changes (same scholar_id)
  useEffect(() => {
    // Only sync when parent actually provides an array; do not clear to [] on undefined/falsy
    if (Array.isArray(record?.scholar_id_type)) {
      setList(record.scholar_id_type);
    }
  }, [record?.scholar_id_type]);

  // ------------------------------
  // Add form state and logic
  // ------------------------------
  const [addType, setAddType] = useState('');
  const [addValue, setAddValue] = useState('');
  const addDisabled = submitting || !addType || !addValue.trim() || !r?.scholar_id;

  // ------------------------------
  // Shared API helpers
  // ------------------------------
  const wpHeaders = useMemo(() => ({
    'X-WP-Nonce': typeof rmVars !== 'undefined' ? rmVars.api_nonce : '',
    'Content-Type': 'multipart/form-data',
  }), []);

  const baseUrl = typeof rmVars !== 'undefined' ? rmVars.restApi : '/scholar/wp-json';
  const finalUrl = baseUrl.replace(/\/rm\/v1\/?$/, '') + '/rm/v1/my_library/info';

  // Ensure GET uses the same origin as POST when rmVars.restApi is defined
  const absGet = async (path) => {
    if (typeof rmVars !== 'undefined' && rmVars.restApi) {
      const root = rmVars.restApi.replace(/\/rm\/v1\/?$/, '');
      return get(root + path);
    }
    return get(path);
  };

  // --- User routes for IDs: /rm/v1/user/scholar/{record_id}/ids/{action}
  const apiRoot = (typeof rmVars !== 'undefined' && rmVars.restApi) ? rmVars.restApi.replace(/\/$/, '') : '/wp-json/rm/v1';
  const buildUserUrl = (action) => {
    const rid = encodeURIComponent(r?.scholar_id ?? '');
    return `${apiRoot}/user/scholar/${rid}/ids/${action}`;
  };
  const postUserIds = async (action, payload) => {
    const fd = new FormData();
    // include body params (WP will also provide record_id from path param; include redundantly for backend convenience)
    Object.keys(payload || {}).forEach(k => fd.append(k, payload[k] == null ? '' : payload[k]));
    const url = buildUserUrl(action);
    const res = await axios.post(url, fd, { headers: wpHeaders });
    return res?.data;
  };

  const onAdd = async () => {
    if (addDisabled) return;
    try {
      setSubmitting(true);
      setErrorMsg('');
      const res = await postUserIds('insert', { id_type: addType, id_value: addValue.trim() });
      if (res?.status === true) {
        // 새로고침 요구사항: 추가 후 전체 페이지 리로드
        if (typeof window !== 'undefined' && window.location) {
          window.location.reload();
          return; // prevent further state changes
        }
        // Fallback (should rarely execute if reload happens)
        setAddValue('');
      } else {
        setErrorMsg(res?.message || '추가에 실패했습니다. (status=false)');
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.message || '추가에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  // ------------------------------
  // Inline edit/delete for list rows (always-editable inputs)
  // ------------------------------
  const onChangeRowValue = (rowId, value) => {
    setList(prev => (Array.isArray(prev) ? prev.map(it => (it.ID === rowId ? { ...it, id_value: value } : it)) : prev));
  };

  const onSaveRow = async (row) => {
    if (!r?.scholar_id || !row?.ID) return;
    const value = (row?.id_value || '').toString().trim();
    // 현재 값 백업 (실패 시 롤백)
    const prev = (list.find(it => it.ID === row.ID)?.id_value ?? '').toString();
    try {
      setSubmitting(true);
      setErrorMsg('');
      const res = await postUserIds('update', { ID: row.ID, id_type: row.id_type, id_value: value });
      if (res?.status === true) {
        // 성공: 화면상 변경 유지
      } else {
        // 실패: 롤백
        setList(prevList => prevList.map(it => it.ID === row.ID ? { ...it, id_value: prev } : it));
        setErrorMsg(res?.message || '수정에 실패했습니다. (status=false)');
      }
    } catch (e) {
      // 실패: 롤백
      setList(prevList => prevList.map(it => it.ID === row.ID ? { ...it, id_value: prev } : it));
      setErrorMsg(e?.response?.data?.message || e?.message || '수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const onDeleteRow = async (row) => {
    if (!r?.scholar_id || !row?.ID) return;
    try {
      setSubmitting(true);
      setErrorMsg('');
      const res = await postUserIds('delete', { ID: row.ID, id_type: row.id_type, id_value: (row?.id_value ?? '') });
      if (res?.status === true) {
        // 성공 후 로컬에서 제거
        setList(prev => (Array.isArray(prev) ? prev.filter(it => it.ID !== row.ID) : prev));
      } else {
        setErrorMsg(res?.message || '삭제에 실패했습니다. (status=false)');
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.message || '삭제에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {showAdd && (
        <div className="mt-6 p-6 lg:p-8 mb-6 bg-[#F3F5F9] rounded-[12px]">
          <p className="mb-6 text-[16px] font-semibold">Researcher ID 추가하기</p>

          <div className="flex flex-col xl:flex-row xl:flex-wrap gap-[14px] lg:gap-3">
            {/* 유형 */}
            <div className="flex flex-col gap-[10px] w-full xl:w-[204px]">
              <span className="text-[14px] font-medium lg:text-[#6C7687]">유형</span>
              <div className="w-full">
                <select
                  className="w-full h-[44px] px-4 pr-[40px] border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none appearance-none text-line-1 cursor-pointer"
                  style={{
                    backgroundImage: `url(${typeof rmVars !== 'undefined' ? rmVars.themeUrl : ''}images/expand_more_6C7687.png)`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '16px',
                    backgroundPosition: 'calc(100% - 16px)',
                  }}
                  name="id_type"
                  value={addType}
                  onChange={(e) => setAddType(e.target.value)}
                >
                  {addOptions.map(opt => (
                    <option key={opt.value || '__placeholder'} value={opt.value} disabled={opt.value === ''} hidden={opt.value === ''}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 아이디 */}
            <div className="flex flex-col gap-[10px] w-full xl:w-[calc(100%-216px)]">
              <span className="text-[14px] font-medium lg:text-[#6C7687]">아이디</span>
              <div className="w-full">
                <input
                  type="text"
                  className="w-full h-[44px] px-4 border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none bg-white"
                  placeholder="ID를 입력하세요"
                  value={addValue}
                  onChange={(e) => setAddValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onAdd(); }}
                />
              </div>
            </div>

            {/* 추가하기 버튼 */}
            <div className="flex flex-col items-center w-full lg:mt-3">
              {errorMsg && (
                <p className="mb-2 text-[13px] text-red-500">{errorMsg}</p>
              )}
              <button
                type="button"
                className={`w-full lg:w-[120px] h-[44px] text-[14px] font-medium text-white rounded-[12px] ${addDisabled ? 'bg-[#C9CEDA] cursor-not-allowed' : 'bg-[#6C7687] cursor-pointer'}`}
                onClick={(e) => { e.preventDefault(); onAdd(); }}
                disabled={addDisabled}
              >
                {submitting ? '처리 중...' : '추가하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 추가된 Researcher ID 리스트 */}
      <div className="flex flex-col gap-4">
        {errorMsg && !showAdd && (
          <p className="text-[13px] text-red-500">{errorMsg}</p>
        )}

        {Array.isArray(list) && list.length > 0 ? (
          list.map((row) => {
            const displayLabel = row?.label || labelMap[row?.id_type] || row?.id_type || '';
            return (
              <div key={row.ID || `${row.id_type}-${row.id_value}`} className="flex gap-1 xl:gap-3">
                <div className="flex-shrink-0 w-[140px] xl:w-[204px]">
                  <select
                    value={displayLabel}
                    disabled
                    className="w-full h-[44px] px-4 pr-[40px] border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none appearance-none text-line-1"
                    style={{
                      backgroundImage: `url(${typeof rmVars !== 'undefined' ? rmVars.themeUrl : ''}images/expand_more_6C7687.png)`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '16px',
                      backgroundPosition: 'calc(100% - 16px)',
                    }}
                  >
                    <option value="" disabled hidden>Select</option>
                    <option value={displayLabel}>{displayLabel}</option>
                  </select>
                </div>

                <div className="w-[calc(100%-192px)] xl:w-[calc(100%-286px)]">
                  <input
                    type="text"
                    className="w-full h-[44px] px-4 border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none bg-white"
                    value={row?.id_value || ''}
                    onChange={(e) => onChangeRowValue(row.ID, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') onSaveRow(row); }}
                  />
                </div>

                {/* Save button */}
                <div className="flex-shrink-0 w-[44px] xl:w-[58px] h-[44px] bg-[#F3F5F9] border border-[#E4E8F1] rounded-[12px]">
                  <button
                    type="button"
                    className={`flex justify-center items-center w-full h-full ${submitting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                    onClick={(e) => { e.preventDefault(); if (!submitting) onSaveRow(row); }}
                    disabled={submitting}
                  >
                    <span className="text-[14px] font-medium text-[#6C7687]">저장</span>
                  </button>
                </div>

                {/* Delete button */}
                <div className="flex-shrink-0 w-[44px] xl:w-[58px] h-[44px] bg-[#F3F5F9] border border-[#E4E8F1] rounded-[12px]">
                  <button
                    type="button"
                    className={`flex justify-center items-center w-full h-full ${submitting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                    onClick={(e) => { e.preventDefault(); if (!submitting) onDeleteRow(row); }}
                    disabled={submitting}
                  >
                    <img src={typeof rmVars !== 'undefined' ? (rmVars.themeUrl + 'images/trash-ico.png') : ''} className="xl:hidden w-5" alt="delete" />
                    <span className="hidden xl:block text-[14px] font-medium text-[#6C7687]">삭제</span>
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="noresult flex justify-center items-center !h-[160px] !cursor-default text-[13px] font-semibold border border-[#E4E8F1] rounded-[12px]">
            <div>추가된 Research ID가 아직 없습니다.</div>
          </div>
        )}
      </div>
    </>
  );
}
